import React, { memo, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { fetchAdminAnalytics, fetchAdminRevenueOverview, fetchAdminSsaOverview } from "../services/adminService";
import { listAdminUsers } from "../services/adminWorkflowService";
import { listAdminDeposits, listAdminTransactions, listAdminWithdrawals } from "../services/paymentService";
import { useAuth } from "../store/AuthContext";
import { hasAdminModuleAccess } from "../utils/adminPermissions";
import {
  getProjectLifecycleLabel,
  normalizeProjectStatus,
  projectTitleWithCode,
  sortProjectsNewestFirst,
} from "../utils/projectViews";

const KPI_SKELETON_KEYS = Array.from({ length: 16 }, (_, i) => `sk-${i}`);

const IconFunded = memo(function IconFunded() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
});

const IconFundraising = memo(function IconFundraising() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  );
});

const IconRedeemed = memo(function IconRedeemed() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12l3 3 5-6" />
    </svg>
  );
});

const IconRevenue = memo(function IconRevenue() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
});

const RevenueKpi = memo(function RevenueKpi({ label, value, detail, tone = "default", title, Icon }) {
  return (
    <div className={`kpi kpi--revenue kpi--revenue-${tone}`} title={title}>
      <div className="kpi-project-head">
        {Icon && (
          <span className="kpi-project-icon" aria-hidden>
            <Icon />
          </span>
        )}
        <div className="kpi-label">{label}</div>
      </div>
      <div className="kpi-value kpi-value--currency">BDT {value}</div>
      {detail && <p className="kpi-revenue-detail muted">{detail}</p>}
    </div>
  );
});

const fmtShareQtyPlain = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const AdminSsaParticularsCell = memo(function AdminSsaParticularsCell({ row }) {
  const tf = num(row?.transfer_fee_shares, 0);
  const rf = num(row?.redemption_fee_shares, 0);
  const rd = num(row?.redeemed_shares_to_ssa, 0);
  const held = num(row?.shares_held, 0);
  const credits = num(row?.cumulative_ssa_credits_shares, tf + rf + rd);
  const xferCount = num(row?.transfer_fee_settlement_count, 0);
  const matches = row?.holdings_match_cumulative_credits !== false && Math.abs(credits - held) < 0.005;

  const lines = [
    {
      label: "Transfer fees → SSA",
      value: tf,
      detail: xferCount > 0 ? `${xferCount} transfer${xferCount === 1 ? "" : "s"}` : null,
    },
    { label: "Redemption fees → SSA", value: rf },
    { label: "Redeemed shares → SSA", value: rd },
  ];

  return (
    <div className="admin-ssa-particulars" title="How shares were credited to the System Shareholder Account for this project">
      {lines.map((line) => (
        <div key={line.label} className="admin-ssa-particulars__line">
          <span className="admin-ssa-particulars__label">{line.label}</span>
          <span className="admin-ssa-particulars__value">
            {fmtShareQtyPlain(line.value)} sh
            {line.detail ? <span className="muted"> · {line.detail}</span> : null}
          </span>
        </div>
      ))}
      <div className="admin-ssa-particulars__equation" aria-label="SSA composition">
        <span className="muted">=</span>{" "}
        <strong>{fmtShareQtyPlain(tf)}</strong>
        <span className="muted"> + </span>
        <strong>{fmtShareQtyPlain(rf)}</strong>
        <span className="muted"> + </span>
        <strong>{fmtShareQtyPlain(rd)}</strong>
        <span className="muted"> = </span>
        <strong>{fmtShareQtyPlain(credits)} sh</strong>
        <span className="muted"> credited</span>
      </div>
      {!matches ? (
        <p className="admin-ssa-particulars__note muted">
          Current SSA holding is {fmtShareQtyPlain(held)} sh (may differ slightly from cumulative credits if data was
          adjusted).
        </p>
      ) : (
        <p className="admin-ssa-particulars__note muted">Matches current SSA holding for this project.</p>
      )}
    </div>
  );
});

const AdminRevenueProjectTable = memo(function AdminRevenueProjectTable({ rows, fmtBdt }) {
  const sortedRows = useMemo(
    () =>
      sortProjectsNewestFirst((rows || []).filter((r) => normalizeProjectStatus(r?.status) !== "CANCELLED")),
    [rows]
  );
  if (!sortedRows.length) {
    return (
      <p className="muted" style={{ margin: "16px 0 0", fontSize: 13 }}>
        No projects to display.
      </p>
    );
  }
  return (
    <div className="admin-dashboard-revenue-projects" style={{ marginTop: 18 }}>
      <h4 style={{ margin: "0 0 6px" }}>Project financials</h4>
      <p className="muted" style={{ margin: "0 0 12px", fontSize: 13 }}>
        Ledger-derived fund position and approved P&amp;L totals per project (including draft and review). Cancelled
        listings are omitted. Operational expenses use the asset-manager expense ledger; P&amp;L revenue/expenses are
        from approved P&amp;L submissions.
      </p>
      <div className="table-wrap admin-dashboard-revenue-table-wrap">
        <table className="history-data-table admin-dashboard-revenue-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Status</th>
              <th>Funds raised</th>
              <th>P&amp;L revenue</th>
              <th>P&amp;L expenses</th>
              <th>Net P&amp;L</th>
              <th>Op. expenses</th>
              <th>Fee income</th>
              <th>Redemptions paid</th>
              <th>Remaining fund</th>
              <th>Ultimate P&amp;L</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const title = projectTitleWithCode({
                name: row.project_name,
                project_code: row.project_code,
              });
              const st = normalizeProjectStatus(row.status);
              return (
                <tr
                  key={row.project_id}
                  className={`admin-dashboard-revenue-table__row${
                    row.is_closed ? " admin-dashboard-revenue-table__row--closed" : ""
                  }`}
                >
                  <td className="admin-dashboard-revenue-table__project">
                    <strong>{title}</strong>
                    {row.asset_manager_name ? (
                      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                        AM: {row.asset_manager_name}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <span className={`badge badge-${st}`}>{getProjectLifecycleLabel(row.status)}</span>
                    {row.expenses_locked ? (
                      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                        Expenses locked
                      </div>
                    ) : null}
                  </td>
                  <td>{fmtBdt(row.funds_raised_bdt)}</td>
                  <td>{fmtBdt(row.pnl_revenue_bdt)}</td>
                  <td>{fmtBdt(row.pnl_expenses_bdt)}</td>
                  <td>{fmtBdt(row.net_pnl_bdt)}</td>
                  <td>{fmtBdt(row.operational_expenses_bdt)}</td>
                  <td>{fmtBdt(row.transfer_fee_income_bdt)}</td>
                  <td>{fmtBdt(row.redemption_paid_bdt)}</td>
                  <td>{fmtBdt(row.remaining_project_fund_bdt)}</td>
                  <td>
                    <strong>{fmtBdt(row.ultimate_pnl_bdt)}</strong>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
});

const SsaKpi = memo(function SsaKpi({ label, value, detail, tone = "default", title, Icon, currency = false }) {
  return (
    <div className={`kpi kpi--ssa kpi--ssa-${tone}`} title={title}>
      <div className="kpi-project-head">
        {Icon && (
          <span className="kpi-project-icon" aria-hidden>
            <Icon />
          </span>
        )}
        <div className="kpi-label">{label}</div>
      </div>
      <div className={currency ? "kpi-value kpi-value--currency" : "kpi-value"}>{currency ? `BDT ${value}` : value}</div>
      {detail && <p className="kpi-revenue-detail muted">{detail}</p>}
    </div>
  );
});

const ProjectStatKpi = memo(function ProjectStatKpi({ label, value, tone, title, Icon }) {
  return (
    <div className={`kpi kpi--project-stage kpi--tone-${tone}`} title={title}>
      <div className="kpi-project-head">
        <span className="kpi-project-icon" aria-hidden>
          <Icon />
        </span>
        <div className="kpi-label">{label}</div>
      </div>
      <div className="kpi-value">{value}</div>
    </div>
  );
});

function KpiSkeleton() {
  return (
    <div className="kpi kpi--skeleton" aria-hidden>
      <div className="kpi-skeleton kpi-skeleton--label" />
      <div className="kpi-skeleton kpi-skeleton--value" />
    </div>
  );
}

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const isSuperAdmin = String(user?.role || "").toUpperCase() === "SUPER_ADMIN";
  const [analytics, setAnalytics] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [ssa, setSsa] = useState(null);
  const [users, setUsers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [message, setMessage] = useState("");
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [ssaLoading, setSsaLoading] = useState(false);
  const [analyticsFailed, setAnalyticsFailed] = useState(false);
  const [revenueFailed, setRevenueFailed] = useState(false);
  const [ssaFailed, setSsaFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOverviewLoading(true);
    setAnalyticsFailed(false);
    const overviewRequests = [];
    if (hasAdminModuleAccess(user, "DASHBOARD")) {
      overviewRequests.push(fetchAdminAnalytics());
    }
    if (hasAdminModuleAccess(user, "CONTROL")) {
      overviewRequests.push(listAdminUsers());
    }
    if (hasAdminModuleAccess(user, "PAYMENTS")) {
      overviewRequests.push(listAdminDeposits(), listAdminWithdrawals(), listAdminTransactions());
    }
    if (!overviewRequests.length) {
      setOverviewLoading(false);
      return undefined;
    }
    Promise.allSettled(overviewRequests).then((results) => {
      if (cancelled) return;
      let idx = 0;
      const next = () => results[idx++];
      if (hasAdminModuleAccess(user, "DASHBOARD")) {
        const analyticsResult = next();
        setAnalytics(analyticsResult.status === "fulfilled" ? analyticsResult.value?.data ?? null : null);
        setAnalyticsFailed(analyticsResult.status === "rejected");
      }
      if (hasAdminModuleAccess(user, "CONTROL")) {
        const usersResult = next();
        setUsers(usersResult.status === "fulfilled" ? usersResult.value?.data ?? [] : []);
      }
      if (hasAdminModuleAccess(user, "PAYMENTS")) {
        const depResult = next();
        const wdResult = next();
        const txnResult = next();
        setDeposits(depResult.status === "fulfilled" ? depResult.value?.data ?? [] : []);
        setWithdrawals(wdResult.status === "fulfilled" ? wdResult.value?.data ?? [] : []);
        setTransactions(txnResult.status === "fulfilled" ? txnResult.value?.data ?? [] : []);
      }
      const hasForbidden = results.some(
        (result) => result.status === "rejected" && result.reason?.response?.status === 403
      );
      if (hasForbidden) {
        setMessage("Some metrics are hidden for your role.");
      } else if (hasAdminModuleAccess(user, "DASHBOARD") && results[0]?.status === "rejected") {
        setMessage("System overview counts from analytics could not be loaded. Other figures may still be shown.");
      } else {
        setMessage("");
      }
      setOverviewLoading(false);
    }).catch(() => {
      if (!cancelled) {
        setOverviewLoading(false);
        setMessage("Failed to load system overview.");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let cancelled = false;
    setRevenueLoading(true);
    setSsaLoading(true);
    setRevenueFailed(false);
    setSsaFailed(false);
    Promise.allSettled([fetchAdminRevenueOverview(), fetchAdminSsaOverview()]).then((results) => {
      if (cancelled) return;
      if (results[0].status === "fulfilled") setRevenue(results[0].value.data);
      else {
        setRevenue(null);
        setRevenueFailed(true);
      }
      if (results[1].status === "fulfilled") setSsa(results[1].value.data);
      else {
        setSsa(null);
        setSsaFailed(true);
      }
      setRevenueLoading(false);
      setSsaLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  const totalInvestors = useMemo(
    () => users.filter((u) => u.role === "INVESTOR").length,
    [users]
  );

  const walletMetrics = useMemo(() => {
    const pendingDeposits = deposits.filter((d) => d.status === "PENDING").length;
    const pendingWithdrawals = withdrawals.filter((w) => w.status === "PENDING").length;
    const approvedDepositAmount = deposits
      .filter((d) => d.status === "APPROVED")
      .reduce((sum, d) => sum + Number(d.amount || 0), 0);
    const processingWithdrawalAmount = withdrawals
      .filter((w) => w.status === "PROCESSING")
      .reduce((sum, w) => sum + Number(w.amount || 0), 0);
    const completedWithdrawalAmount = withdrawals
      .filter((w) => w.status === "COMPLETED")
      .reduce((sum, w) => sum + Number(w.amount || 0), 0);
    const companyWalletNet = approvedDepositAmount - (processingWithdrawalAmount + completedWithdrawalAmount);
    return {
      pendingDeposits,
      pendingWithdrawals,
      approvedDepositAmount,
      processingWithdrawalAmount,
      completedWithdrawalAmount,
      companyWalletNet,
    };
  }, [deposits, withdrawals]);

  const overallActivity = useMemo(
    () =>
      num(analytics?.total_investments, 0) +
      deposits.length +
      withdrawals.length +
      transactions.length,
    [analytics?.total_investments, deposits.length, withdrawals.length, transactions.length]
  );

  const fundedCount = num(analytics?.projects_funded_count, 0);
  const fundraisingCount = num(analytics?.projects_fundraising_count, 0);
  const redeemedCount = num(analytics?.projects_redeemed_count, 0);

  const revenueRates = revenue?.configured_rates || {};
  const fmtBdt = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : "0.00";
  };
  const fmtShares = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? `${n.toFixed(2)} sh` : "0.00 sh";
  };

  return (
    <div className="page admin-dashboard-page">
      <h2>Admin Dashboard</h2>
      <p style={{ margin: 0 }} className="muted">
        <strong>{user?.full_name}</strong>
      </p>
      {message && <p className="msg">{message}</p>}
      <div className="card admin-dashboard-overview" style={{ padding: 14 }}>
        <h3>System Overview</h3>
        <div className="kpis">
          {overviewLoading ? (
            KPI_SKELETON_KEYS.map((k) => <KpiSkeleton key={k} />)
          ) : (
            <>
              <div className="kpi">
                <div className="kpi-label">Total Investors</div>
                <div className="kpi-value">{totalInvestors}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Direct Referrals</div>
                <div className="kpi-value">{analyticsFailed ? 0 : num(analytics?.referral_direct_count, 0)}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Company Root Referrals</div>
                <div className="kpi-value">{analyticsFailed ? 0 : num(analytics?.referral_company_root_count, 0)}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Unassigned Referrals</div>
                <div className="kpi-value">{analyticsFailed ? 0 : num(analytics?.referral_unassigned_count, 0)}</div>
              </div>
              <ProjectStatKpi
                label="Funded projects"
                value={analyticsFailed ? 0 : fundedCount}
                tone="funded"
                title="Full subscription reached: projects in Lock-in or Matured (capital deployed, not in fundraising)."
                Icon={IconFunded}
              />
              <ProjectStatKpi
                label="Fundraising projects"
                value={analyticsFailed ? 0 : fundraisingCount}
                tone="fundraising"
                title="Listings open for investment: Open, Funding, or Paused (fundraising)."
                Icon={IconFundraising}
              />
              <ProjectStatKpi
                label="Redeemed projects"
                value={analyticsFailed ? 0 : redeemedCount}
                tone="redeemed"
                title="Projects in Redeemed or Closed status (payout / lifecycle completed)."
                Icon={IconRedeemed}
              />
              <div className="kpi">
                <div className="kpi-label">Pending Deposits</div>
                <div className="kpi-value">{walletMetrics.pendingDeposits}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Pending Withdrawals</div>
                <div className="kpi-value">{walletMetrics.pendingWithdrawals}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Pending Redeem Requests</div>
                <div className="kpi-value" title="Investor sell-to-treasury requests awaiting Operations review.">
                  {hasAdminModuleAccess(user, "OPERATIONS") ? (
                    <Link to="/admin/control?tab=REDEMPTIONS">
                      {analyticsFailed ? 0 : num(analytics?.pending_redemptions_count, 0)}
                    </Link>
                  ) : (
                    <span>{analyticsFailed ? 0 : num(analytics?.pending_redemptions_count, 0)}</span>
                  )}
                </div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Company wallet inflow</div>
                <div className="kpi-value kpi-value--currency">BDT {walletMetrics.approvedDepositAmount.toFixed(2)}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Company wallet outflow</div>
                <div className="kpi-value kpi-value--currency">
                  BDT {(walletMetrics.processingWithdrawalAmount + walletMetrics.completedWithdrawalAmount).toFixed(2)}
                </div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Company wallet net</div>
                <div className="kpi-value kpi-value--currency">BDT {walletMetrics.companyWalletNet.toFixed(2)}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">System Activity</div>
                <div className="kpi-value">{overallActivity}</div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Pending email verification</div>
                <div className="kpi-value">
                  <Link to="/admin/users?status=PENDING_VERIFICATION">
                    {analyticsFailed ? 0 : num(analytics?.pending_verification_users, 0)}
                  </Link>
                </div>
              </div>
              <div className="kpi">
                <div className="kpi-label">Suspended Users</div>
                <div className="kpi-value">{analyticsFailed ? 0 : num(analytics?.suspended_users, 0)}</div>
              </div>
            </>
          )}
        </div>
      </div>

      {isSuperAdmin && (
        <div className="card admin-dashboard-revenue" style={{ padding: 14, marginTop: 16 }}>
          <div className="admin-dashboard-revenue-header">
            <div>
              <h3 style={{ margin: 0 }}>Revenue</h3>
              <p className="muted admin-dashboard-revenue-intro">
                Cash-based platform earnings only: profitable P&amp;L admin fees, transfer fees collected from investor
                wallets, and withdrawal fees. Share-settled fees are tracked under SSA.
              </p>
            </div>
            <div className="admin-dashboard-revenue-links muted">
              <Link to="/admin/control?tab=SETTINGS">Fee settings</Link>
              <span aria-hidden> · </span>
              <Link to="/admin/financial-report">Financial report</Link>
            </div>
          </div>

          {revenueLoading ? (
            <div className="kpis admin-dashboard-revenue-kpis">
              {Array.from({ length: 4 }, (_, i) => (
                <KpiSkeleton key={`rev-sk-${i}`} />
              ))}
            </div>
          ) : revenueFailed ? (
            <p className="msg">Revenue totals could not be loaded.</p>
          ) : (
            <>
              <div className="admin-dashboard-revenue-total" title="Sum of all revenue sources below">
                <span className="admin-dashboard-revenue-total-label">Total platform revenue</span>
                <span className="admin-dashboard-revenue-total-value">BDT {fmtBdt(revenue?.total_platform_revenue_bdt)}</span>
              </div>
              <div className="kpis admin-dashboard-revenue-kpis">
                <RevenueKpi
                  label="Admin fee earned from projects"
                  value={fmtBdt(revenue?.admin_fee_earned_bdt)}
                  detail={`${num(revenue?.admin_fee_profitable_pnl_count, 0)} profitable approved P&L · ${revenueRates.pnl_admin_fee_pct ?? "0"}% rate`}
                  tone="admin-fee"
                  title="Admin fee from approved P&L where revenue exceeds expenses (positive gross profit only)."
                  Icon={IconFunded}
                />
                <RevenueKpi
                  label="Transfer fee earned (cash)"
                  value={fmtBdt(revenue?.transfer_fee_earned_bdt ?? revenue?.share_transfer_fee_cash_bdt)}
                  detail={`${num(revenue?.share_transfer_fee_count, 0)} approved transfers · ${revenueRates.share_transfer_fee_pct ?? "0"}% rate`}
                  tone="transfer"
                  title="Cash portion of transfer fees debited from investor current accounts."
                  Icon={IconFundraising}
                />
                <RevenueKpi
                  label="Withdrawal fee earned"
                  value={fmtBdt(revenue?.withdrawal_fee_total_bdt)}
                  detail={`${num(revenue?.withdrawal_completed_count, 0)} completed withdrawals · ${revenueRates.wallet_withdrawal_fee_pct ?? "0"}% + BDT ${fmtBdt(revenueRates.wallet_withdrawal_fee_fixed_bdt)} fixed`}
                  tone="withdrawal"
                  title="Fees on completed investor withdrawal requests."
                  Icon={IconRevenue}
                />
              </div>
              <AdminRevenueProjectTable rows={revenue?.project_financials} fmtBdt={fmtBdt} />
            </>
          )}
        </div>
      )}

      {isSuperAdmin && (
        <div className="card admin-dashboard-ssa" style={{ padding: 14, marginTop: 16 }}>
          <div className="admin-dashboard-revenue-header">
            <div>
              <h3 style={{ margin: 0 }}>System Shareholder Account (SSA)</h3>
              <p className="muted admin-dashboard-revenue-intro">
                Shares credited when fees are settled from holdings (transfer fee shortfall, redemption fees, and
                redeemed shares). SSA is a shareholder; project share cap is unchanged.
              </p>
            </div>
          </div>

          {ssaLoading ? (
            <div className="kpis admin-dashboard-revenue-kpis">
              {Array.from({ length: 4 }, (_, i) => (
                <KpiSkeleton key={`ssa-sk-${i}`} />
              ))}
            </div>
          ) : ssaFailed ? (
            <p className="msg">SSA totals could not be loaded.</p>
          ) : (
            <div className="kpis admin-dashboard-revenue-kpis">
              <SsaKpi
                label="Shares from transfer fees"
                value={fmtShares(ssa?.transfer_fee_shares)}
                detail={`Notional BDT ${fmtBdt(ssa?.transfer_fee_notional_bdt)} · ${num(ssa?.transfer_fee_settlement_count, 0)} transfers`}
                tone="transfer"
                title="Share-component transfer fees credited to SSA (when wallet balance was insufficient)."
                Icon={IconFundraising}
              />
              <SsaKpi
                label="Shares from redemption fees"
                value={fmtShares(ssa?.redemption_fee_shares)}
                detail={`Notional BDT ${fmtBdt(ssa?.redemption_fee_notional_bdt)}`}
                tone="redeem-fee"
                title="Per-transaction redemption fees settled in shares to SSA (never from cash)."
                Icon={IconRevenue}
              />
              <SsaKpi
                label="Shares from redemptions"
                value={fmtShares(ssa?.redeemed_shares_to_ssa)}
                detail="Redeemed investor shares held by SSA"
                tone="redeemed"
                title="Shares moved to SSA when redemptions are approved."
                Icon={IconRedeemed}
              />
              <SsaKpi
                label="Total SSA holdings (NAV)"
                value={fmtBdt(ssa?.total_treasury_nav_bdt)}
                detail={`${fmtBdt(ssa?.total_treasury_shares_held)} shares across ${num(ssa?.projects_with_ssa_holdings, 0)} projects`}
                tone="total"
                currency
                title="Current NAV of all SSA share holdings."
                Icon={IconFunded}
              />
            </div>
          )}

          {!ssaLoading && !ssaFailed && (
            <div className="admin-dashboard-ssa-projects" style={{ marginTop: 18 }}>
              <h4 style={{ margin: "0 0 6px" }}>SSA holdings by project</h4>
              <p className="muted" style={{ margin: "0 0 12px", fontSize: 13 }}>
                Projects at <strong>100% SSA</strong> (all issued shares in treasury, no investor holdings) are highlighted.
                Close redeemed projects from{" "}
                <Link to="/admin/control?tab=PROJECTS">Admin Control → Redeemed &amp; closed projects</Link>.
              </p>
              {(ssa?.project_holdings || []).length === 0 ? (
                <p className="muted" style={{ margin: 0 }}>
                  No SSA share holdings yet. Transfer-fee and redemption activity will appear here per project.
                </p>
              ) : (
                <div className="table-wrap admin-dashboard-ssa-table-wrap">
                  <table className="history-data-table admin-dashboard-ssa-table">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Status</th>
                        <th>% of cap</th>
                        <th>NAV (BDT)</th>
                        <th>SSA particulars</th>
                        <th className="admin-dashboard-ssa-table__total">Total SSA (sh)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ssa?.project_holdings || []).map((row) => {
                        const title = projectTitleWithCode({
                          name: row.project_name,
                          project_code: row.project_code,
                        });
                        const st = normalizeProjectStatus(row.status);
                        const fullyRedeemed = Boolean(row.ssa_fully_redeemed);
                        return (
                          <tr
                            key={row.project_id}
                            className={`admin-dashboard-ssa-table__project-row${
                              fullyRedeemed ? " admin-dashboard-ssa-table__project-row--fully-redeemed" : ""
                            }`}
                          >
                            <td className="admin-dashboard-ssa-table__project-name">
                              <strong>{title}</strong>
                              {fullyRedeemed ? (
                                <div style={{ marginTop: 4 }}>
                                  <span className="badge badge-REDEEMED">100% SSA</span>
                                </div>
                              ) : null}
                            </td>
                            <td>
                              {getProjectLifecycleLabel(row.status)}
                              {fullyRedeemed && st !== "REDEEMED" && st !== "CLOSED" ? (
                                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                                  Awaiting Redeemed status
                                </div>
                              ) : null}
                            </td>
                            <td>{num(row.pct_of_cap, 0).toFixed(2)}%</td>
                            <td>{fmtBdt(row.nav_bdt)}</td>
                            <td className="admin-dashboard-ssa-table__particulars">
                              <AdminSsaParticularsCell row={row} />
                            </td>
                            <td className="admin-dashboard-ssa-table__total">
                              <strong>{fmtShares(row.shares_held)}</strong>
                              {fullyRedeemed && row.share_cap ? (
                                <div className="muted" style={{ fontSize: 11, fontWeight: 400 }}>
                                  of {fmtShares(row.share_cap)} cap
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
